import { createState } from "@/state.init";
import { Investigation } from "./models";
import * as api from "./api";
import { UseToast, ToastType } from "@/components/root/toast/toast.state";
import { UseLoading } from "@/common/composition/loading";
import { POPULATION_TYPE } from "@/common/models";

const TMP_INV_ID_PREFIX = "tmp";

const { m, getter, action, beforeEachAction, afterEachAction } = createState(
  "investigation",
  {
    isLoading: false,
    investigations: [] as Investigation[],
    currentInvestigation: null,
  }
);

const isLoading = getter((s) => s.isLoading);
const setLoading = m.set("isLoading");

beforeEachAction(({ mutate }) => mutate(setLoading, true));
afterEachAction(({ mutate }) => mutate(setLoading, false));

const investigations = m.crud<Investigation, "investigations">(
  "investigations",
  "id"
);

const has_investigation = (subject_id: string) =>
  investigations.read.value.findIndex(
    (inv: Investigation) => (inv as Investigation).objectId === subject_id
  ) > -1;

const getInvestigations = action<void>(
  "get investigations",
  async ({ mutate }) => {
    const res = await api.investigations.list();
    if (res.success) {
      console.log("res.value::", res.value);
      mutate(investigations.set, res.value);
    }
  }
);

const {
  isLoading: isActionInProgress,
  setLoading: changeActionInProgressState,
} = UseLoading(false);

const createInvestigation = action<{
  objectId: string;
  name: string;
  email?: string;
  type: POPULATION_TYPE;
  cluster_id?: string;
  shared: boolean;
}>("create investigation", async ({ mutate }, inv) => {
  changeActionInProgressState(true);
  const res = await api.investigations.create({
    ...inv,
    owned: true,
    createdAt: new Date().toISOString(),
  });
  if (res.success) {
    mutate(investigations.append, [
      {
        ...inv,
        objectId: inv.objectId,
        owned: true,
        shared: inv.shared,
        createdAt: new Date().toISOString(),
        id: res.value,
      },
    ]);
    UseToast().show({
      type: ToastType.SUCCESS,
      msg: inv.name + " was successfully added to your watch list",
      duration: 3000,
    });
  }
  changeActionInProgressState(false);
});

const updateInvestigation = action<{
  id: string;
  name: string;
  shared: boolean;
}>("update investigation", async ({ mutate }, inv) => {
  changeActionInProgressState(true);
  const updated_inv: Investigation = {
    ...inv,
    ...investigations.read.value.find((s) => s.objectId === inv.id)!,
  };
  const res = await api.investigations.update(updated_inv);
  if (res.success) {
    mutate(investigations.update, updated_inv);
  } else {
    UseToast().show({
      type: ToastType.ALERT,
      msg: "Failed to update investigation",
      duration: 3000,
    });
  }
  changeActionInProgressState(false);
});

const deleteInvestigation = action<{ id: string }>(
  "delete investigation",
  async ({ mutate }, { id }) => {
    changeActionInProgressState(true);
    const inv = investigations.read.value.find((d) => d.id === id)!;
    const res = await api.investigations.remove(inv.id);
    if (res.success) {
      mutate(investigations.remove, {
        ...inv,
      });
    }
    changeActionInProgressState(false);
  }
);
const deleteInvestigationBySubjectId = action<string>(
  "delete investigation",
  async ({ dispatch }, subject_id) => {
    changeActionInProgressState(true);
    const inv = investigations.read.value.find(
      (f) => f.objectId === subject_id
    );
    if (!inv) {
      console.log("NO SUCH INV WITH ID :", subject_id);
      return;
    }
    dispatch(deleteInvestigation, inv);
  }
);

const currentInvestigation = getter((s) => s.currentInvestigation);
const _setCurrentInvestigation = m.set("currentInvestigation");
const setCurrentInvestigation = action<Investigation>(
  "set current investigation",
  async ({ mutate }, item) => {
    const res = await api.investigations.isExist(item.id);
    if (res.success) {
      mutate(_setCurrentInvestigation, item);
    } else {
      if (res.code === 404) {
        UseToast().show({
          type: ToastType.ALERT,
          msg: "sorry..the item was deleted just now",
        });
      }
    }
  }
);

const isTempInvestigation = getter((s) => {
  return (
    s.currentInvestigation &&
    s.currentInvestigation.id.startsWith(TMP_INV_ID_PREFIX)
  );
});

const sharing = getter((s) => s.currentInvestigation?.shared);

const init = action<string>("init investigation", async ({ dispatch }, id) => {
  // @ts-ignore
  await dispatch(getInvestigations);
  const inv = investigations.read.value.find((inv) => inv.id === id);
  if (inv) {
    await dispatch(setCurrentInvestigation, inv);
  }
});
export const state = {
  init,
  isLoading,
  // investigations
  investigations: investigations.read,
  has_investigation,
  isActionInProgress,
  createInvestigation,
  updateInvestigation,
  deleteInvestigation,
  deleteInvestigationBySubjectId,
  getInvestigations,
  // current investigation
  currentInvestigation,
  isTempInvestigation,
  setCurrentInvestigation,
  // sharing
  sharing,
};
